import type { ItemStatus, ItemType, Visibility } from '../api/types';
import { getLocale, t, type MsgKey } from '../i18n';

/** Display order of content types, shared by every type picker. */
export const TYPE_ORDER: ItemType[] = ['movie', 'series', 'book', 'game', 'course', 'article', 'recipe', 'video', 'document', 'website', 'other'];

const TYPE_KEYS: Record<ItemType, MsgKey> = {
  movie: 'type.movie',
  series: 'type.series',
  book: 'type.book',
  game: 'type.game',
  course: 'type.course',
  article: 'type.article',
  recipe: 'type.recipe',
  video: 'type.video',
  document: 'type.document',
  website: 'type.website',
  other: 'type.other',
};

const VISIBILITY_KEYS: Record<Visibility, MsgKey> = {
  private: 'vis.private',
  followers: 'vis.followers',
  public: 'vis.public',
};

const VISIBILITY_HINT_KEYS: Record<Visibility, MsgKey> = {
  private: 'vis.private.hint',
  followers: 'vis.followers.hint',
  public: 'vis.public.hint',
};

export const VISIBILITY_ORDER: Visibility[] = ['private', 'followers', 'public'];

export function typeLabel(type: ItemType): string {
  return t(TYPE_KEYS[type] ?? TYPE_KEYS.other);
}

/** Built per render: a module-level list would freeze the language of its labels. */
export function typeOptions(): { value: ItemType; label: string }[] {
  return TYPE_ORDER.map((value) => ({ value, label: typeLabel(value) }));
}

export function visibilityLabel(visibility: Visibility): string {
  return t(VISIBILITY_KEYS[visibility]);
}

/** Replaces the old `VISIBILITIES` constant from api/client.ts, so the picker is not frozen in one language. */
export function visibilityOptions(): { value: Visibility; label: string; hint: string }[] {
  return VISIBILITY_ORDER.map((value) => ({ value, label: visibilityLabel(value), hint: t(VISIBILITY_HINT_KEYS[value]) }));
}

/**
 * Progress on a saved link. The server stores one of four universal slugs; only the word shown
 * depends on the type, so re-typing a card never invalidates what was already marked.
 */
const STATUS_SLUGS = ['wishlist', 'active', 'done', 'dropped'] as const;

type StatusGroup = 'read' | 'watch' | 'play' | 'cook' | 'save';

const STATUS_GROUP: Partial<Record<ItemType, StatusGroup>> = {
  book: 'read',
  course: 'read',
  article: 'read',
  document: 'read',
  movie: 'watch',
  series: 'watch',
  video: 'watch',
  game: 'play',
  recipe: 'cook',
  website: 'save',
  other: 'save',
};

const STATUS_KEYS: Record<StatusGroup, Record<ItemStatus, MsgKey>> = {
  read: { wishlist: 'status.read.wishlist', active: 'status.read.active', done: 'status.read.done', dropped: 'status.read.dropped' },
  watch: { wishlist: 'status.watch.wishlist', active: 'status.watch.active', done: 'status.watch.done', dropped: 'status.watch.dropped' },
  play: { wishlist: 'status.play.wishlist', active: 'status.play.active', done: 'status.play.done', dropped: 'status.play.dropped' },
  cook: { wishlist: 'status.cook.wishlist', active: 'status.cook.active', done: 'status.cook.done', dropped: 'status.cook.dropped' },
  save: { wishlist: 'status.save.wishlist', active: 'status.save.active', done: 'status.save.done', dropped: 'status.save.dropped' },
};

export function statusLabel(type: ItemType, status: ItemStatus): string {
  return t(STATUS_KEYS[STATUS_GROUP[type] ?? 'save'][status]);
}

export function statusOptions(type: ItemType): { value: ItemStatus; label: string }[] {
  return STATUS_SLUGS.map((value) => ({ value, label: statusLabel(type, value) }));
}

export function isItemStatus(value: string): value is ItemStatus {
  return (STATUS_SLUGS as readonly string[]).includes(value);
}

export function itemsWord(n: number): string {
  return t('count.items', { n });
}

export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(getLocale() === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMin = Math.round((Date.now() - then) / 60000);
  if (diffMin < 1) return t('time.now');
  if (diffMin < 60) return t('time.minute', { n: diffMin });
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) return t('time.hour', { n: diffHours });
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return t('time.yesterday');
  if (diffDays < 7) return t('time.day', { n: diffDays });
  return formatDate(iso);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const letters = parts.map((part) => part.charAt(0).toUpperCase()).join('');
  return letters || '·';
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** Phones hand back "youtube.com/watch?v=…" without a scheme; the API wants a real URL. */
export function normalizeLinkInput(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return /^[\p{L}\p{N}-]+(\.[\p{L}\p{N}-]+)+(:\d+)?([/?#]\S*)?$/u.test(trimmed) ? `https://${trimmed}` : trimmed;
}

export function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;
}

/**
 * The server answers with a stable code and a Russian message. Russian keeps showing exactly what
 * the server wrote — the dictionary must not silently re-edit copy a user already reads. English
 * maps the code, and a validation failure is composed from the offending field and zod reason.
 */
const ERROR_CODE_KEYS: Record<string, MsgKey> = {
  UNAUTHORIZED: 'err.UNAUTHORIZED',
  FORBIDDEN: 'err.FORBIDDEN',
  NOT_FOUND: 'err.NOT_FOUND',
  CONFLICT: 'err.CONFLICT',
  ALREADY_EXISTS: 'err.ALREADY_EXISTS',
  ITEM_DUPLICATE: 'err.ITEM_DUPLICATE',
  INVALID_CREDENTIALS: 'err.INVALID_CREDENTIALS',
  EMAIL_TAKEN: 'err.EMAIL_TAKEN',
  CSRF_MISMATCH: 'err.CSRF_MISMATCH',
  VALIDATION: 'err.VALIDATION',
  BAD_REQUEST: 'err.BAD_REQUEST',
  BAD_JSON: 'err.BAD_JSON',
  TOO_MANY_ATTEMPTS: 'err.TOO_MANY_ATTEMPTS',
  USERNAME_TAKEN: 'err.USERNAME_TAKEN',
  INTERNAL: 'err.INTERNAL',
};

const ZOD_CODE_KEYS: Record<string, MsgKey> = {
  too_small: 'err.zod.too_small',
  too_big: 'err.zod.too_big',
  invalid_type: 'err.zod.invalid_type',
  invalid_value: 'err.zod.invalid_value',
  invalid_format: 'err.zod.invalid_format',
  custom: 'err.zod.custom',
};

const FIELD_KEYS: Record<string, MsgKey> = {
  identifier: 'field.identifier',
  email: 'field.email',
  password: 'field.password',
  displayName: 'field.displayName',
  username: 'field.username',
  bio: 'field.bio',
  avatarUrl: 'field.avatarUrl',
  url: 'field.url',
  title: 'field.title',
  description: 'field.description',
  imageUrl: 'field.imageUrl',
  coverImageUrl: 'field.coverImageUrl',
  author: 'field.author',
  personalNote: 'field.personalNote',
  blocks: 'field.blocks',
  content: 'field.content',
  currentPassword: 'field.currentPassword',
  newPassword: 'field.newPassword',
  itemId: 'field.itemId',
  position: 'field.position',
  type: 'field.type',
  visibility: 'field.visibility',
  name: 'field.name',
  note: 'field.note',
};

type ApiErrorShape = { code?: unknown; message?: unknown; details?: { issues?: { code?: unknown; field?: unknown }[] } };

function localizedApiError(error: ApiErrorShape): string | null {
  const code = typeof error.code === 'string' ? error.code : '';
  const codeKey = ERROR_CODE_KEYS[code];
  if (!codeKey) return null;
  if (code !== 'VALIDATION') return t(codeKey);
  const issue = error.details?.issues?.[0];
  const reasonKey = issue && typeof issue.code === 'string' ? ZOD_CODE_KEYS[issue.code] : undefined;
  const fieldKey = issue && typeof issue.field === 'string' ? FIELD_KEYS[issue.field] : undefined;
  if (!reasonKey) return t('err.VALIDATION');
  return fieldKey ? `${t(fieldKey)}: ${t(reasonKey)}` : t(reasonKey);
}

export function messageOf(error: unknown, fallback?: string): string {
  if (error && typeof error === 'object') {
    const shape = error as ApiErrorShape;
    if (getLocale() === 'en') {
      const localized = localizedApiError(shape);
      if (localized) return localized;
    }
    if (typeof shape.message === 'string' && shape.message.trim()) return shape.message;
  }
  return fallback ?? t('errors.unknown');
}
